"use client";

import { useState } from "react";
import {
  DataTable,
  Notice,
  PillGroup,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  HINT,
  LABEL,
  WIDGET_H2,
} from "@/components/tool-kit/styles";
import { DataInput } from "@/components/tools/research-design/data-input";
import { LikertChart } from "@/components/tools/research-design/likert-chart";
import { LikertComposite } from "@/components/tools/research-design/likert-composite";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { downloadText } from "@/lib/tool-kit/download";
import {
  codeRange,
  DASH,
  decimals,
  type NumberFormatter,
  percentText,
} from "@/lib/tool-kit/research-design/format";
import {
  highCode,
  itemColumn,
  type LikertItemSummary,
  type LikertPoints,
  type LikertScale,
  readCodes,
  summarizeItem,
} from "@/lib/tool-kit/research-design/likert";
import {
  isNumericCell,
  labelsFor,
  parseTable,
  type TableLayout,
  toCsv,
} from "@/lib/tool-kit/research-design/matrix";

/** English strings. A translated content file overrides these through `ui`. */
export const LIKERT_SCALE_ANALYZER_STRINGS = {
  dataLabel: "Responses — one row per respondent, one column per item",
  dataPlaceholder: "ID\tQ1\tQ2\tQ3\nR01\t4\t5\t2\nR02\t5\t4\t1",
  dataHint:
    "Paste from Excel, Google Sheets or SPSS, or type numbers separated by commas or spaces. Blank cells and NA count as missing; option labels such as Agree are read as their codes.",
  loadExample: "Load example",
  clear: "Clear",
  exampleData:
    "Teacher\tQ1\tQ2\tQ3\tQ4\tQ5\nT01\t4\t5\t2\t4\t3\nT02\t5\t4\t1\t5\t4\nT03\t4\t4\t2\t4\t\nT04\t3\t2\t4\t2\t2\nT05\t4\t4\t2\t5\t4\nT06\t5\t5\t1\t4\t5\nT07\t2\t3\t4\t2\t3\nT08\t4\t3\t3\t4\t3\nT09\t4\t4\t2\t4\t4\nT10\t5\t4\t2\t5\t3\nT11\t3\t3\t3\t3\t2\nT12\t4\t5\t1\t4\t4",
  firstRowHeader: "First row holds item names",
  firstColumnIds: "First column holds respondent IDs",
  showParsed: "Show the table as read",
  previewNote: "Showing the first {shown} of {total} rows.",
  idHeader: "ID",
  readAs: "Read as {respondents} × {items}.",
  respondentCount: "one: {count} respondent|other: {count} respondents",
  itemCount: "one: {count} item|other: {count} items",
  itemFallback: "Item {n}",
  respondentFallback: "Respondent {n}",
  scaleLegend: "Scale points",
  points4: "4-point",
  points5: "5-point",
  points7: "7-point",
  lowLegend: "Codes start at",
  lowOne: "1",
  lowZero: "0",
  labelsHeading: "Option labels",
  labelFor: "Label for code {code}",
  labels4: ["Strongly disagree", "Disagree", "Agree", "Strongly agree"],
  labels5: [
    "Strongly disagree",
    "Disagree",
    "Neither agree nor disagree",
    "Agree",
    "Strongly agree",
  ],
  labels7: [
    "Strongly disagree",
    "Disagree",
    "Somewhat disagree",
    "Neither agree nor disagree",
    "Somewhat agree",
    "Agree",
    "Strongly agree",
  ],
  results: "Results",
  enterPrompt:
    "Paste at least one respondent's answers, or load the example, to see item statistics.",
  missingNote:
    "one: {count} blank or NA cell was treated as a missing answer.|other: {count} blank or NA cells were treated as missing answers.",
  invalidNote:
    "one: {count} cell is not a code on this scale or one of its labels, so it was treated as missing.|other: {count} cells are not codes on this scale or its labels, so they were treated as missing.",
  colItem: "Item",
  colN: "n",
  colMedian: "Median",
  colMode: "Mode",
  colMean: "Mean",
  colSd: "SD",
  colDisagree: "Disagree",
  colNeutral: "Neutral",
  colAgree: "Agree",
  colTopTwo: "Top 2",
  colCount: "{code} ({label})",
  groupsOdd:
    "Disagree = codes {disagree}, neutral = {neutral}, agree = {agree}.",
  groupsEven:
    "Disagree = codes {disagree}, agree = {agree} — a 4-point scale has no neutral option.",
  topTwoNote: "Top 2 = the two highest options ({codes}).",
  ordinalNote:
    "Medians, modes and percentages respect what Likert answers are: ordered categories. Means and SDs treat the codes as equally spaced numbers — common practice, and easier to defend for a composite of several items than for a single item.",
  downloadItems: "Download item statistics (CSV)",
  itemsFile: "likert-item-statistics.csv",
  chartHeading: "Diverging stacked bar chart",
  chartTitle: "Responses by item",
  chartDescription:
    "Each bar is one item. Disagreement extends left of the center line, agreement right, and the neutral option straddles it.",
  segmentTooltip: "{item} — {label}: {percent} ({count})",
  downloadSvg: "Download SVG",
  downloadPng: "Download PNG",
  chartFileSvg: "likert-chart.svg",
  chartFilePng: "likert-chart.png",
  compositeHeading: "Composite scores",
  methodLegend: "Combine items by",
  methodSum: "Sum",
  methodMean: "Mean",
  reverseLegend: "Reverse-code these items first",
  reverseHint:
    "A reversed answer becomes (lowest code + highest code) − answer, so on a 1–5 scale a 2 counts as 4.",
  compositeRule:
    "A sum is given only to respondents who answered every item; a mean averages the items each person answered.",
  scored: "Respondents scored",
  compositeMean: "Mean score",
  compositeSd: "SD",
  compositeMin: "Lowest",
  compositeMax: "Highest",
  showScores: "Show every respondent's score",
  colRespondent: "Respondent",
  colScore: "Score",
  colAnswered: "Items answered",
  downloadScores: "Download composite scores (CSV)",
  scoresFile: "likert-composite-scores.csv",
} as const;

type Strings = typeof LIKERT_SCALE_ANALYZER_STRINGS;
type PointsChoice = "4" | "5" | "7";

const LABEL_SETS = { "4": "labels4", "5": "labels5", "7": "labels7" } as const;

function analyze(
  raw: string,
  layout: Partial<TableLayout>,
  scale: LikertScale,
  labels: readonly string[]
) {
  const lowered = labels.map((label) => label.trim().toLowerCase());
  const isValue = (cell: string) =>
    isNumericCell(cell) || lowered.includes(cell.trim().toLowerCase());
  const table = parseTable(raw, isValue, layout);
  const itemCount = Math.max(0, ...table.body.map((row) => row.length));
  const codes = readCodes(table.body, scale, labels);
  const summaries = Array.from({ length: itemCount }, (_, item) =>
    summarizeItem(itemColumn(codes.values, item), scale)
  );
  return { table, itemCount, codes, summaries };
}

function groupsText(scale: LikertScale, strings: Strings): string {
  const half = Math.floor(scale.points / 2);
  const high = highCode(scale);
  const disagree = codeRange(scale.low, scale.low + half - 1);
  if (scale.points % 2 === 0) {
    return t(strings.groupsEven, {
      disagree,
      agree: codeRange(scale.low + half, high),
    });
  }
  return t(strings.groupsOdd, {
    disagree,
    neutral: String(scale.low + half),
    agree: codeRange(scale.low + half + 1, high),
  });
}

/** The optional columns: neutral on odd scales, top-2-box on the 7-point scale. */
function shareColumns<T>(
  scale: LikertScale,
  disagree: T,
  neutral: T,
  agree: T,
  topTwo: T
): T[] {
  return [
    disagree,
    ...(scale.points % 2 === 1 ? [neutral] : []),
    agree,
    ...(scale.points === 7 ? [topTwo] : []),
  ];
}

function itemRow(
  name: string,
  s: LikertItemSummary,
  scale: LikertScale,
  fmt: NumberFormatter
): string[] {
  return [
    name,
    fmt(s.n),
    s.n === 0 ? DASH : fmt(s.median, { maximumFractionDigits: 1 }),
    s.modes.map((code) => fmt(code)).join(", ") || DASH,
    decimals(fmt, s.mean),
    decimals(fmt, s.sd),
    ...shareColumns(
      scale,
      percentText(fmt, s.disagree),
      percentText(fmt, s.neutral),
      percentText(fmt, s.agree),
      percentText(fmt, s.topTwo)
    ),
  ];
}

export default function LikertScaleAnalyzerWidget() {
  const strings = useToolUi(LIKERT_SCALE_ANALYZER_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [raw, setRaw] = useState("");
  const [header, setHeader] = useState<boolean | null>(null);
  const [ids, setIds] = useState<boolean | null>(null);
  const [points, setPoints] = useState<PointsChoice>("5");
  const [low, setLow] = useState<"1" | "0">("1");
  const [labelEdits, setLabelEdits] = useState<Record<string, string>>({});

  const scale: LikertScale = {
    points: Number(points) as LikertPoints,
    low: Number(low),
  };
  const defaults: readonly string[] = strings[LABEL_SETS[points]];
  const labels = defaults.map(
    (label, i) => labelEdits[`${points}:${i}`] ?? label
  );
  const { table, itemCount, codes, summaries } = analyze(
    raw,
    { header: header ?? undefined, ids: ids ?? undefined },
    scale,
    labels
  );
  const itemNames = labelsFor(table.columns, itemCount, (n) =>
    t(strings.itemFallback, { n })
  );
  const respondentNames = labelsFor(table.ids, table.body.length, (n) =>
    t(strings.respondentFallback, { n })
  );
  const ready = itemCount > 0 && table.body.length > 0;
  const head = [
    strings.colItem,
    strings.colN,
    strings.colMedian,
    strings.colMode,
    strings.colMean,
    strings.colSd,
    ...shareColumns(
      scale,
      strings.colDisagree,
      strings.colNeutral,
      strings.colAgree,
      strings.colTopTwo
    ),
  ];

  function reset(text: string) {
    setRaw(text);
    setHeader(null);
    setIds(null);
  }

  function downloadItems() {
    const counts = labels.map((label, i) =>
      t(strings.colCount, { code: scale.low + i, label })
    );
    const rows = summaries.map((s, item) => [
      itemNames[item],
      s.n,
      s.median,
      s.modes.join(" "),
      s.mean,
      s.sd,
      ...shareColumns(scale, s.disagree, s.neutral, s.agree, s.topTwo),
      ...s.counts,
    ]);
    downloadText(
      strings.itemsFile,
      toCsv([[...head, ...counts], ...rows]),
      "text/csv;charset=utf-8"
    );
  }

  return (
    <>
      <DataInput
        columnNames={itemNames}
        example={strings.exampleData}
        fmt={fmt}
        id="lk-data"
        onChange={setRaw}
        onHeader={setHeader}
        onIds={setIds}
        onReset={reset}
        strings={strings}
        summary={t(strings.readAs, {
          respondents: tPlural(
            strings.respondentCount,
            locale,
            table.body.length,
            { count: fmt(table.body.length) }
          ),
          items: tPlural(strings.itemCount, locale, itemCount, {
            count: fmt(itemCount),
          }),
        })}
        table={table}
        value={raw}
      />

      <section className="space-y-4">
        <div className="flex flex-wrap gap-6">
          <PillGroup
            label={strings.scaleLegend}
            name="lk-points"
            onChange={setPoints}
            options={[
              { value: "4", label: strings.points4 },
              { value: "5", label: strings.points5 },
              { value: "7", label: strings.points7 },
            ]}
            value={points}
          />
          <PillGroup
            label={strings.lowLegend}
            name="lk-low"
            onChange={setLow}
            options={[
              { value: "1", label: strings.lowOne },
              { value: "0", label: strings.lowZero },
            ]}
            value={low}
          />
        </div>
        <fieldset className="space-y-2">
          <legend className={LABEL}>{strings.labelsHeading}</legend>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {labels.map((label, i) => (
              <TextField
                id={`lk-label-${i}`}
                key={`${points}-${scale.low + i}`}
                label={t(strings.labelFor, { code: scale.low + i })}
                onChange={(value) =>
                  setLabelEdits((edits) => ({
                    ...edits,
                    [`${points}:${i}`]: value,
                  }))
                }
                value={label}
              />
            ))}
          </div>
        </fieldset>
      </section>

      <section aria-live="polite" className="space-y-6">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {ready ? (
          <>
            {codes.missing > 0 ? (
              <Notice>
                {tPlural(strings.missingNote, locale, codes.missing, {
                  count: fmt(codes.missing),
                })}
              </Notice>
            ) : null}
            {codes.invalid > 0 ? (
              <Notice>
                {tPlural(strings.invalidNote, locale, codes.invalid, {
                  count: fmt(codes.invalid),
                })}
              </Notice>
            ) : null}
            <DataTable
              head={head}
              rows={summaries.map((summary, item) =>
                itemRow(itemNames[item], summary, scale, fmt)
              )}
            />
            <div className="space-y-1">
              <p className={HINT}>{groupsText(scale, strings)}</p>
              {scale.points === 7 ? (
                <p className={HINT}>
                  {t(strings.topTwoNote, {
                    codes: codeRange(highCode(scale) - 1, highCode(scale)),
                  })}
                </p>
              ) : null}
            </div>
            <Notice>{strings.ordinalNote}</Notice>
            <button
              className={BUTTON_SECONDARY}
              onClick={downloadItems}
              type="button"
            >
              {strings.downloadItems}
            </button>
            <LikertChart
              fmt={fmt}
              itemNames={itemNames}
              labels={labels}
              scale={scale}
              strings={strings}
              summaries={summaries}
            />
            <LikertComposite
              fmt={fmt}
              itemNames={itemNames}
              respondentNames={respondentNames}
              scale={scale}
              strings={strings}
              values={codes.values}
            />
          </>
        ) : (
          <Notice>{strings.enterPrompt}</Notice>
        )}
      </section>
    </>
  );
}
